import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ColumnConfig } from './useColumnVisibility';
import { listingSettingsService, type ListingKey } from '../services/listingSettings';

type ColumnWidthMap = Record<string, number>;

type SetColumnWidthByKey = Dispatch<SetStateAction<ColumnWidthMap>>;

export const useListingColumnWidthsPersistence = <T extends ColumnConfig>(
  listingKey: ListingKey,
  baseColumns: T[],
  setColumnWidthByKey: SetColumnWidthByKey
) => {
  const allowedKeys = useMemo(() => new Set(baseColumns.map((col) => String(col.key))), [baseColumns]);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const settings = await listingSettingsService.getListingSettings(listingKey);
        if (cancelled || !settings?.column_widths) {
          return;
        }
        const filtered: ColumnWidthMap = {};
        Object.entries(settings.column_widths).forEach(([key, value]) => {
          if (!allowedKeys.has(key)) {
            return;
          }
          if (typeof value !== 'number' || !Number.isFinite(value)) {
            return;
          }
          filtered[key] = Math.max(50, Math.round(value));
        });
        if (!Object.keys(filtered).length) {
          return;
        }
        setColumnWidthByKey((prev) => ({ ...prev, ...filtered }));
      } catch {
        // Ignore loading errors and fall back to defaults
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [allowedKeys, listingKey, setColumnWidthByKey]);

  const persistColumnWidths = useCallback(
    (next: ColumnWidthMap) => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(async () => {
        try {
          await listingSettingsService.updateListingSettings(listingKey, {
            column_widths: next,
          });
        } catch {
          // Ignore persistence errors
        }
      }, 500);
    },
    [listingKey]
  );

  return { persistColumnWidths };
};
